import { useMemo, useState } from "react";
import { Controller, useForm, type Resolver } from "react-hook-form";
import {
  Button,
  DataTable,
  Modal,
  SectionCard,
  Select,
  type ColumnDef,
  type SelectOption,
} from "@/deps/ui-library";
import { useAccounts, useChartRoles, useMapChartRole } from "@/api/queries";
import type { Account, ChartRole } from "@/api/types";

/** The option that adds the supplied chart's account. No account id can look like it. */
const ADD_NEW = "add-new";

interface Values {
  choice: string;
}

const resolver: Resolver<Values> = (values) => {
  const errors = values.choice
    ? {}
    : { choice: { type: "required", message: "Choose the account." } };
  return { values: values.choice ? values : {}, errors };
};

const first = (value: SelectOption | SelectOption[] | null) =>
  String((Array.isArray(value) ? value[0] : value)?.value ?? "");

/** What the dialog starts on: the current account, the supplied code, or adding it. */
function startingChoice(role: ChartRole, accounts: readonly Account[]) {
  if (role.accountId) return role.accountId;
  return accounts.find((account) => account.code === role.suppliedCode)?.id ?? ADD_NEW;
}

function SetAccountDialog({
  role,
  accounts,
  onClose,
}: {
  role: ChartRole;
  accounts: readonly Account[];
  onClose: () => void;
}) {
  const map = useMapChartRole();
  const candidates = accounts.filter((account) => account.group === role.group && account.isActive);
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<Values>({
    resolver,
    defaultValues: { choice: startingChoice(role, candidates) },
  });

  return (
    <Modal.Form
      open
      title={`Set the account for ${role.suppliedName}`}
      subtitle="New postings for this purpose go to the account you choose. What's already posted stays where it is."
      submitLabel="Save"
      busy={map.isPending}
      error={map.error ? (map.error as Error).message : null}
      onClose={onClose}
      onSubmit={() =>
        void handleSubmit(({ choice }) =>
          map.mutate(
            { role: role.role, accountId: choice === ADD_NEW ? null : choice },
            { onSuccess: onClose },
          ),
        )()
      }
    >
      <Controller
        control={control}
        name="choice"
        render={({ field }) => (
          <Select
            required
            label="Account"
            options={[
              ...(role.accountId
                ? []
                : [{ value: ADD_NEW, label: `Add ${role.suppliedName} as a new account` }]),
              ...candidates.map((account) => ({
                value: account.id,
                label: `${account.code} ${account.name}`,
              })),
            ]}
            value={field.value}
            showClearButton={false}
            errorText={errors.choice?.message}
            onChange={(details) => field.onChange(first(details.value))}
          />
        )}
      />
    </Modal.Form>
  );
}

/**
 * Which account answers when a document needs one for a purpose. A new company gets all of them from
 * its chart. One set up on an older chart can be missing a few, and this is where it fills them in.
 */
export function ChartRolesCard() {
  const roles = useChartRoles();
  const { data: accounts, isPending: accountsPending, isError: accountsFailed } = useAccounts();
  const data = roles.data;
  // The dialog starts from the chart, so nothing can be opened before the chart is here.
  const isPending = roles.isPending || accountsPending;
  const isError = roles.isError || accountsFailed;
  const [editing, setEditing] = useState<ChartRole | null>(null);

  const chart = useMemo(() => accounts ?? [], [accounts]);
  // What needs doing comes first.
  const rows = useMemo(
    () => [...(data ?? [])].sort((a, b) => Number(!!a.accountId) - Number(!!b.accountId)),
    [data],
  );

  const columns = useMemo<Array<ColumnDef<ChartRole>>>(
    () => [
      {
        id: "purpose",
        header: "Used for",
        cell: ({ row: { original } }) => original.suppliedName,
      },
      {
        id: "account",
        header: "Account",
        cell: ({ row: { original } }) => {
          const account = chart.find((candidate) => candidate.id === original.accountId);
          if (account) return `${account.code} ${account.name}`;
          return original.accountId ? "" : <span className="text-destructive-text">Not set</span>;
        },
      },
      {
        id: "set",
        header: "",
        meta: { textAlign: "right" as const },
        cell: ({ row: { original } }) => (
          <Button
            colorScheme="default"
            variant="ghost"
            size="xs"
            aria-label={`Set the account for ${original.suppliedName}`}
            onClick={() => setEditing(original)}
          >
            {original.accountId ? "Change" : "Set"}
          </Button>
        ),
      },
    ],
    [chart],
  );

  return (
    <SectionCard title="Accounts by purpose" flush>
      {isPending ? (
        <p className="text-muted-foreground p-4 text-sm">Loading</p>
      ) : isError ? (
        <p className="text-destructive-text p-4 text-sm">Couldn't load these. Try again shortly.</p>
      ) : (
        <DataTable
          aria-label="Accounts by purpose"
          size="sm"
          enableSorting={false}
          columns={columns}
          data={rows}
          getRowId={(row) => row.role}
          pagination={{ mode: "manual", show: false }}
        />
      )}
      {editing ? (
        <SetAccountDialog role={editing} accounts={chart} onClose={() => setEditing(null)} />
      ) : null}
    </SectionCard>
  );
}
